import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import { PublicKey } from '@solana/web3.js';
import strings from '../localization';
import { useSendTransaction } from '../utils/notifications';
import { findAssociatedTokenAddress } from '../utils/tokens';
import { WRAPPED_DOMI_MINT } from '../utils/tokens/instructions';
import {
  refreshWalletPublicKeys,
  useWallet,
  useWalletAddressForMint,
} from '../utils/wallet';
import DialogForm from './DialogForm';

const SATOMIS = 10 ** 9;

// Held back when wrapping so the account can still pay fees and, when a new
// wDOMI account has to be created, its rent deposit.
const WRAP_FEE_RESERVE = 0.05 * SATOMIS;

export default function WrapDomiDialog({
  open,
  onClose,
  publicKey,
  balanceInfo,
}) {
  const wallet = useWallet();
  const [sendTransaction, sending] = useSendTransaction();
  const [transferAmountString, setTransferAmountString] = useState('');

  const { amount: balanceAmount, mint } = balanceInfo;
  const isUnwrap = !!mint;

  // Existing wDOMI account, if any, so a second wrap tops it up instead of
  // scattering balances over multiple accounts.
  const existingWdomiAddress = useWalletAddressForMint(
    isUnwrap ? null : WRAPPED_DOMI_MINT,
  );

  const maxAmount = isUnwrap
    ? balanceAmount
    : Math.max(balanceAmount - WRAP_FEE_RESERVE, 0);

  // An older (non-associated) wDOMI account gets folded into the ATA as
  // part of the wrap — worth telling the user before they sign.
  const willConsolidate =
    !isUnwrap &&
    !!existingWdomiAddress &&
    existingWdomiAddress !==
      findAssociatedTokenAddress(wallet.publicKey, WRAPPED_DOMI_MINT).toBase58();
  const parsedAmount = Math.round(parseFloat(transferAmountString) * SATOMIS);
  const validAmount = parsedAmount > 0 && parsedAmount <= maxAmount;

  function onSubmit() {
    const makeTransaction = isUnwrap
      ? wallet.unwrapDomi(publicKey, parsedAmount, balanceAmount)
      : wallet.wrapDomi(
          parsedAmount,
          existingWdomiAddress ? new PublicKey(existingWdomiAddress) : null,
        );

    sendTransaction(makeTransaction, {
      onSuccess: () => {
        refreshWalletPublicKeys(wallet);
        setTransferAmountString('');
        onClose();
      },
    });
  }

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        {isUnwrap ? strings.unwrapDomiTitle : strings.wrapDomiTitle}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isUnwrap ? strings.unwrapDomiInfo : strings.wrapDomiInfo}
        </DialogContentText>
        <TextField
          label={strings.amount}
          fullWidth
          variant="outlined"
          margin="normal"
          type="number"
          value={transferAmountString}
          onChange={(e) => setTransferAmountString(e.target.value.trim())}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  onClick={() =>
                    setTransferAmountString(String(maxAmount / SATOMIS))
                  }
                >
                  {strings.max}
                </Button>
              </InputAdornment>
            ),
            inputProps: { step: 'any', min: 0 },
          }}
          helperText={
            <span
              onClick={() =>
                setTransferAmountString(String(maxAmount / SATOMIS))
              }
            >
              <Link href="#" underline="always">
                {strings.balance}: {maxAmount / SATOMIS}{' '}
                {isUnwrap ? 'wDOMI' : 'DOMI'}
              </Link>
            </span>
          }
        />
        {isUnwrap && validAmount && parsedAmount >= balanceAmount ? (
          <DialogContentText variant="body2">
            {strings.unwrapDomiCloseNote}
          </DialogContentText>
        ) : null}
        {willConsolidate ? (
          <DialogContentText variant="body2">
            {strings.wrapDomiConsolidateNote}
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.cancel}</Button>
        <Button
          type="submit"
          color="primary"
          disabled={sending || !validAmount}
        >
          {isUnwrap ? strings.unwrap : strings.wrap}
        </Button>
      </DialogActions>
    </DialogForm>
  );
}
